import React from 'react';
import PropTypes from 'prop-types';
import { css } from 'emotion';
import { Button } from 'antd';
import Flexbox from 'flexbox-react';
import Wizard from 'components/core/wizard/Wizard';
import ProcessService from 'services/ProcessService';
import ProcessUtils from 'utils/ProcessUtils';
import ComponentUtils from 'utils/ComponentUtils';
import ProcessValidator from 'utils/ProcessValidator';
import DataExplorerService from 'services/DataExplorerService';
import DataExplorerPage from 'components/core/data_explorer/DataExplorerPage';
import ProcessInformationPage from 'components/page/process/wizard/ProcessInformationPage';
import InputsPage from 'components/page/process/wizard/InputsPage';
import OutputPage from 'components/page/process/wizard/OutputPage';
import DimensionsPage from 'components/page/process/wizard/DimensionsPage';
import TransformationPage from 'components/page/process/wizard/TransformationPage';
import InputQCPage from 'components/page/process/wizard/InputQCPage';
import OutputQCPage from 'components/page/process/wizard/OutputQCPage';
import RunPage from 'components/page/process/wizard/RunPage';
import RollupError from 'components/core/RollupError';
import Constants from 'utils/Constants';


export default class ProcessWizard extends Wizard {

    static defaultProps = {
        title: '',
    };

    static propTypes = Object.assign({
        process: PropTypes.object, // the process from the store
        processId: PropTypes.string, // the id of the process we are editing in the store
        userLoggedIn: PropTypes.bool,
        hasWritePermissions: PropTypes.bool,
        processErrors: PropTypes.object, // the process validation errors
        explorerData: PropTypes.object, // the results of the data explorer for all input datastreams
        siteFacilites: PropTypes.array,
        dods: PropTypes.array,
        dodsById: PropTypes.object,
        exploredDatastreams: PropTypes.object
    }, Wizard.propTypes);

    static PAGE_INFO = { id: 'info', label: 'Describe Process' };
    static PAGE_INPUTS = { id: 'inputs', label: 'Define Input' };
    static PAGE_OUTPUTS = { id: 'outputs', label: 'Setup Output File' };
    static PAGE_DIMENSIONS = { id: 'dimensions', label: 'Dimension Shape' };
    static PAGE_TRANSFORMATION = { id: 'transformation', label: 'Define Transformation' };
    static PAGE_QC_INPUT = { id: 'qc_input', label: 'Input QC' };
    static PAGE_QC_OUTPUT = { id: 'qc_output', label: 'Output QC' };
    static PAGE_RUN = { id: 'run', label: 'Run Data Consolidator' };

    constructor(props) {
        super(props);

        // Clear the data explorer state from local storage
        DataExplorerPage.resetStateForProcess(props.processId);
    }

    getChildProps() {
        return Object.assign({}, super.getChildProps(), {
            process: this.props.process,
            processId: this.props.processId,
            name: this.props.process.name,
            userLoggedIn: this.props.userLoggedIn,
            hasWritePermissions: this.props.hasWritePermissions,
            processErrors: this.props.processErrors,
            siteFacilities: this.props.siteFacilities,
            dods: this.props.dods,
            dodsById: this.props.dodsById,
            showImportWizard: this.props.showImportWizard,
            onProcessRenamed: this.props.onProcessRenamed,
            exploredDatastreams: this.props.exploredDatastreams
        });
    }

    getSteps() {
        return this.getStepsAndPages().steps;
    }

    addPage = (stepsList, pageList, component, page, dimensionName = null) => {
        pageList.push({ component, step: page.id, stepPos: this.stepPos, dimensionName });

        const stepKey = dimensionName ? `${page.id}-${dimensionName}` : page.id;

        stepsList.push({
            key: stepKey,
            subPage: dimensionName,
            title: React.createElement(CustomStep, { stepKey, stepPos: this.stepPos, stepTitle: page.label, dimensionName, processId: this.props.processId, processErrors: this.props.processErrors })
        });
        this.stepPos += 1;
    };

    getStepsAndPages() {
        this.stepPos = 0;
        const pages = [];
        const steps = [];

        this.addPage(steps, pages, ProcessInformationPage, ProcessWizard.PAGE_INFO);

        this.addPage(steps, pages, InputsPage, ProcessWizard.PAGE_INPUTS);

        this.addPage(steps, pages, OutputPage, ProcessWizard.PAGE_OUTPUTS);

        ProcessUtils.getOutputDimensionsList(this.props.process).forEach(dimName => {
            this.addPage(steps, pages, DimensionsPage, ProcessWizard.PAGE_DIMENSIONS, dimName);
        });

        ProcessUtils.getOutputDimensionsList(this.props.process).forEach(dimName => {
            this.addPage(steps, pages, TransformationPage, ProcessWizard.PAGE_TRANSFORMATION, dimName);
        });

        if (this.props.process && ProcessUtils.hasRelevantInputQCParameters(this.props.process, this.props.exploredDatastreams)) {
            this.addPage(steps, pages, InputQCPage, ProcessWizard.PAGE_QC_INPUT);
        }

        if (this.props.process) {
            const coordinateSystem = [ProcessUtils.getWizardCoordinateSystemName(this.props.process)];
            if (coordinateSystem && coordinateSystem[0] !== "") {
                ProcessUtils.getNonPassthroughDimensionNames(this.props.process, ProcessUtils.getWizardCoordinateSystemName(this.props.process)).forEach(dimName => {
                    let shouldDisplay = false;
                    const parameters = ['std_ind_max', 'std_bad_max', 'goodfrac_ind_min', 'goodfrac_bad_min'];

                    parameters.forEach(parameterName => {
                        shouldDisplay = shouldDisplay || ProcessUtils.isRelevantTransformationParameter(this.props.process, parameterName, coordinateSystem, dimName, this.props.exploredDatastreams);
                    });

                    if (shouldDisplay) {
                        this.addPage(steps, pages, OutputQCPage, ProcessWizard.PAGE_QC_OUTPUT, dimName);
                    }
                });
            }
        }

        this.addPage(steps, pages, RunPage, ProcessWizard.PAGE_RUN);

        return { steps, pages };
    }

    getPages() {
        return this.getStepsAndPages().pages;
    }

    hasStepError(stepName, dimensionName) {
        let hasError = false;
        const process = this.props.process;
        const errors = this.props.processErrors;

        // Check the validation errors for the process.
        // opening existing process in wizard, has moment when process is not defined while loading process
        // Also there's a moment where errors may not be defined because validator hasn't run yet after fetch.
        if (process && ProcessUtils.hasVariableRetrieval(process) && errors) {
            const errorList = ProcessValidator.getWizardStepRollupErrorList(errors, stepName, dimensionName);
            const maxError = ProcessValidator.getRollupError(errorList, process.name);
            if (maxError && maxError.level === Constants.LEVEL_ERROR) {
                hasError = true;
            }
        }

        return hasError;
    }

    isMissingRequiredInfo = (pageId) => {
        let missingRequiredInfo = false;
        if (pageId === ProcessWizard.PAGE_INFO.id) {
            if (!this.props.processErrors || this.props.processErrors.name || this.props.processErrors.run_locations) {
                missingRequiredInfo = true;
            }

        } else if (pageId === ProcessWizard.PAGE_INPUTS.id) {
            if (Object.keys(this.props.process.variable_retrieval.retrieved_variables).length === 0) {
                missingRequiredInfo = true;
            }
        }
        return missingRequiredInfo;
    };

    /**
     * If page depends on explorer data, checks if explorer is finished.  If it is
     * still running, then page is not ready.
     */
    isPageReady(pageId) {
        const explorerDependentPages = [
            ProcessWizard.PAGE_DIMENSIONS.id,
            ProcessWizard.PAGE_TRANSFORMATION.id,
            ProcessWizard.PAGE_QC_INPUT.id,
            ProcessWizard.PAGE_QC_OUTPUT.id
        ];
        let ready = true;
        if (explorerDependentPages.indexOf(pageId) > -1) {

            // If we are on the first dimensions page and there are more dimensions, then we need to check explorer
            if (!(pageId === ProcessWizard.PAGE_DIMENSIONS.id)) {
                ready = !DataExplorerService.isDataBeingExplored(this.props.process.name);
            }
        }
        if (!ready) {
            this.nextTooltip = 'The data explorer must finish before navigating further';
        }
        return ready;
    }

    canGoForward() {
        let pageReady = true;
        const currentPage = this.getCurrentPage();
        if (currentPage.id === ProcessWizard.PAGE_DIMENSIONS.id) {
            pageReady = this.isPageReady(ProcessWizard.PAGE_TRANSFORMATION.id);
        }
        return super.canGoForward() && pageReady;
    }

    canGoToPage(pageIndex) {
        const page = this.getPages()[pageIndex];
        return super.canGoToPage(pageIndex) && this.isPageReady(page.id);
    }


    beforeFinish = () => {
        // rename coordinate system to something more meaningful
        // use default name until this point so we can have the most information for creating this name
        // and don't have to have logic to check whether the name should be updated and it isn't visible to use in wizard mode anyway
        const editor = ProcessUtils.getEditor(this.props.process);
        editor.renameWizardCoordinateSystemDefault();
        editor.commit();
    };

    onSave = () => {
        this.beforeFinish();

        ProcessService.saveProcess(this.props.process.name);
    };

    onNext() {
        const doNext = () => {
            super.onNext();
        };

        // If we are on the first page (which has an RIEComboBox for Run Locations), we have to delay the next
        // button because RIEComboBox has a setTimeout of 200 ms in the onBlur to finishEditing.  (This is to work
        // around a problem with Select.Creatable.)  So if the user clicks directly from the RIEComboBox to the
        // next button, if the next button isn't delayed, it will destroy the current page before the RIEComboBox calls
        // finishEditing, so their last changes will not be committed.  If we can find a better select component, we
        // might be able to get rid of this hack.
        if (this.state.currentPageIndex === 0) {
            setTimeout(() => {
                doNext();
            }, 300);
        } else {
            doNext();
        }
    }

    renderNavigationHeader() {
        const iconColor = this.props.process.production ? 'green' : 'yellow';
        const iconTooltip = this.props.process.production ? 'Production (DMF)' : 'Development';

        return (
            <Flexbox className={`process-title ${css`align-items: center;padding-right: 10px;`}`}>
                <i className={`fa fa-cogs ${iconColor}`} aria-hidden="true" title={iconTooltip}/>
                <span className="process-label">Process:</span>
                {this.props.process.name}
            </Flexbox>
        );
    }

    renderAfterNavButtons() {
        return (
            <div>
                {ComponentUtils.renderPermissionDependentComponent(
                    <Button type="primary" onClick={this.onSave} disabled={!this.props.hasWritePermissions}>
                        Save
                    </Button>,
                    this.props.hasWritePermissions)
                }
            </div>
        );
    }
}

class CustomStep extends React.Component {
    static propTypes = {
        stepKey: PropTypes.string,
        stepPos: PropTypes.number,
        stepTitle: PropTypes.string,
        dimensionName: PropTypes.string,
        processId: PropTypes.string,
        processErrors: PropTypes.object
    };

    render() {
        let label = `${this.props.stepPos + 1}. ${this.props.stepTitle}`;
        if (this.props.dimensionName) {
            label = `${label}: `;
        }
        return (
            <span data-step-key={this.props.stepKey}>
                <RollupError
                    fieldPath={['wizard', this.props.stepKey, this.props.dimensionName]}
                    processErrors={this.props.processErrors}
                    processId={this.props.processId}
                />
                {label}
                {this.props.dimensionName &&
                    <span className={'step-dimension-label'}>{this.props.dimensionName}</span>
                }
            </span>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/wizard/ProcessWizard.js